import React from 'react';
import { Box, LinearProgress, Typography, alpha, useTheme } from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import usePixExpiracao from '../../Hooks/usePixExpiracao';

type Variant = 'card' | 'inline';

type Props = {
  dataExpiracao?: string | null;
  tempoTotalSegundos?: number;
  onExpirar?: () => void;
  compacto?: boolean;
  variant?: Variant;
};

const PixExpiracaoBar: React.FC<Props> = ({
  dataExpiracao,
  tempoTotalSegundos,
  onExpirar,
  compacto = false,
  variant = 'card'
}) => {
  const theme = useTheme();
  const { progresso, restantePercentual, expirado, formatado } = usePixExpiracao({
    dataExpiracao,
    tempoTotalSegundos,
    onExpirar
  });

  if (!dataExpiracao) return null;

  const cor =
    progresso < 50
      ? theme.palette.success.main
      : progresso < 80
      ? theme.palette.warning.main
      : theme.palette.error.main;

  const textoRotulo = expirado ? 'QR Code expirado' : 'Expira em';

  if (variant === 'inline') {
    return (
      <Box role="timer" aria-live="polite">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            mb: '6px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <TimerIcon sx={{ color: cor, fontSize: 18 }} />
            <Typography
              variant={compacto ? 'caption' : 'body2'}
              fontWeight="bold"
              color="text.secondary"
            >
              {textoRotulo}
            </Typography>
          </Box>
          <Typography
            variant={compacto ? 'caption' : 'body2'}
            fontWeight="bold"
            sx={{ color: cor, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatado}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={restantePercentual}
          aria-label="Tempo restante do QR Code PIX"
          sx={{
            height: compacto ? 4 : 6,
            borderRadius: 4,
            bgcolor: alpha(cor, 0.15),
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: `linear-gradient(90deg, ${alpha(cor, 0.7)}, ${cor})`,
              transition: 'width 1s linear'
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      role="timer"
      aria-live="polite"
      sx={{
        p: compacto ? '12px' : '16px',
        borderRadius: 3,
        border: `1px solid ${alpha(cor, 0.3)}`,
        bgcolor: alpha(cor, 0.06)
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          mb: '8px'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <TimerIcon sx={{ color: cor, fontSize: 20 }} />
          <Typography
            variant={compacto ? 'caption' : 'body2'}
            fontWeight="bold"
            color="text.secondary"
          >
            {textoRotulo}
          </Typography>
        </Box>
        <Typography
          variant={compacto ? 'caption' : 'body1'}
          fontWeight="bold"
          sx={{ color: cor, fontVariantNumeric: 'tabular-nums' }}
        >
          {formatado}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={restantePercentual}
        aria-label="Tempo restante do QR Code PIX"
        sx={{
          height: compacto ? 6 : 8,
          borderRadius: 4,
          bgcolor: alpha(cor, 0.15),
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            background: `linear-gradient(90deg, ${alpha(cor, 0.7)}, ${cor})`,
            transition: 'width 1s linear'
          }
        }}
      />
    </Box>
  );
};

export default PixExpiracaoBar;
